/**
 * BCR test page — independent route for BCR test management.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Button,
  Input,
  Space,
  Spin,
  Alert,
  Descriptions,
  Divider,
  Modal,
  message,
  Typography,
  Empty,
  Table,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlayCircleOutlined,
  StopOutlined,
  ExperimentOutlined,
  LinkOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import { useBcrStore } from '@/store/bcrStore';
import { usePackageStore } from '@/store/packageStore';
import { useConfigStore } from '@/store/configStore';
import * as bcrApi from '@/api/bcr';
import { updatePackageCustomArtifacts } from '@/api/bcr';
import BcrSubTaskList from '@/components/bcr/BcrSubTaskList';
import BcrReportLinks from '@/components/bcr/BcrReportLinks';
import BcrArtifacts from '@/components/bcr/BcrArtifacts';
import LogPanel from '@/components/bcr/LogPanel';
import TaskSelectionModal from '@/components/bcr/TaskSelectionModal';
import BatConfigGenerateDrawer from '@/components/bcr/BatConfigGenerateDrawer';
import type { BcrTaskListItem, ComparisonConfig, CustomBswPackage, McuGitInfo } from '@/types';
import { formatDate } from '@/utils';
import * as packageApi from '@/api/packages';
import { getBatConfig } from '@/apis/batConfig';

const { Title, Text } = Typography;

const BcrTestPage = () => {
  const { projectId, packageId } = useParams<{ projectId: string; packageId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTaskId = searchParams.get('taskId');
  const numProjectId = Number(projectId);
  const numPackageId = Number(packageId);

  const {
    currentTask,
    isCreating,
    isLoading,
    isCancelling,
    isRerunning,
    error,
    logs,
    isLogPanelCollapsed,
    setLogPanelCollapsed,
    resetLogs,
    createTask,
    fetchTaskDetail,
    cancelTask,
    rerunSubTask,
    startPolling,
    stopPolling,
    clearError,
    reset,
  } = useBcrStore();

  const { packages, fetchPackages } = usePackageStore();
  const { projects } = useConfigStore();

  const currentProject = projects.find((p) => p.id === numProjectId);
  const basicPackage = packages.find((p) => p.id === numPackageId);

  // Form state
  const [mfPackageName, setMfPackageName] = useState<string>('');
  const [currentPackageDetail, setCurrentPackageDetail] = useState<any>(null);

  // Task selection modal state
  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [pendingTaskData, setPendingTaskData] = useState<any>(null);

  // History tasks
  const [historyTasks, setHistoryTasks] = useState<BcrTaskListItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [latestTaskArtifacts, setLatestTaskArtifacts] = useState<Record<string, any>>();
  const [latestTaskMcuGit, setLatestTaskMcuGit] = useState<McuGitInfo | undefined>();

  // BAT 配置一键生成抽屉
  const [batConfigDrawerOpen, setBatConfigDrawerOpen] = useState(false);
  // 项目是否已有运行配置 → 决定入口按钮文案（生成 / 更新）
  const [hasBatConfig, setHasBatConfig] = useState(false);

  // Custom artifacts — stored at package level (shared across all BCR tasks for the same BSW version)
  const getPackageCustomArtifacts = useCallback(() => {
    if (currentPackageDetail?.custom_artifacts) {
      return currentPackageDetail.custom_artifacts;
    }
    return [];
  }, [currentPackageDetail]);

  // RPA requirement creation
  const [isCreatingRpa, setIsCreatingRpa] = useState(false);
  const [rpaModalVisible, setRpaModalVisible] = useState(false);

  // Load packages for old version dropdown
  useEffect(() => {
    if (numProjectId) {
      fetchPackages({ project_id: numProjectId, page: 1, page_size: 200 });
    }
  }, [numProjectId, fetchPackages]);

  // Load current package detail with files
  useEffect(() => {
    if (numPackageId) {
      packageApi
        .getPackage(numPackageId)
        .then((detail) => {
          setCurrentPackageDetail(detail);
        })
        .catch((error) => {
          console.error('Failed to load package detail:', error);
        });
    }
  }, [numPackageId]);

  // Use currentPackageDetail with files if available, otherwise fall back to basicPackage
  const currentPackage = currentPackageDetail || basicPackage;

  // 探测项目是否已有运行配置，决定入口按钮文案（生成 / 更新）
  const refreshBatConfigExistence = useCallback(async () => {
    const projectName = currentProject?.name;
    if (!projectName) {
      setHasBatConfig(false);
      return;
    }
    try {
      const config = await getBatConfig(projectName);
      setHasBatConfig(!!config);
    } catch {
      setHasBatConfig(false);
    }
  }, [currentProject?.name]);

  useEffect(() => {
    refreshBatConfigExistence();
  }, [refreshBatConfigExistence]);

  // Refresh only the history list (no auto-switch to active task)
  const refreshHistoryList = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const result = await bcrApi.listBcrTasks(numProjectId, {
        package_id: numPackageId,
        page: 1,
        page_size: 50,
      });
      setHistoryTasks(result.results);

      // Load the latest BCR task's artifacts to display before create form.
      // 列表已按 -created_at 倒序，results[0] 即最新任务；不限定 finished，
      // 避免最新任务是 stop 时回退展示到更早的 finished 任务。
      const latestTask = result.results[0];
      if (latestTask) {
        const taskDetail = await bcrApi.getBcrTaskDetail(numProjectId, latestTask.id);
        setLatestTaskArtifacts(taskDetail.uploaded_artifacts);
        setLatestTaskMcuGit(taskDetail.mcu_git);
      } else {
        setLatestTaskArtifacts(undefined);
      }
    } catch {
      // Silent fail
      setLatestTaskArtifacts(undefined);
    } finally {
      setHistoryLoading(false);
    }
  }, [numProjectId, numPackageId]);

  // Load history and auto-select active task (only on mount)
  // If _preferredTaskId is provided, load that specific task instead of auto-detecting.
  const loadExistingTask = useCallback(async (_preferredTaskId?: number | null) => {
    setHistoryLoading(true);
    try {
      const result = await bcrApi.listBcrTasks(numProjectId, {
        package_id: numPackageId,
        page: 1,
        page_size: 50,
      });
      setHistoryTasks(result.results);

      // Load the latest BCR task's artifacts to display before create form.
      // 列表已按 -created_at 倒序，results[0] 即最新任务；不限定 finished，
      // 避免最新任务是 stop 时回退展示到更早的 finished 任务。
      const latestTask = result.results[0];
      if (latestTask) {
        const taskDetail = await bcrApi.getBcrTaskDetail(numProjectId, latestTask.id);
        setLatestTaskArtifacts(taskDetail.uploaded_artifacts);
        setLatestTaskMcuGit(taskDetail.mcu_git);
      } else {
        setLatestTaskArtifacts(undefined);
      }

      // Determine which task to load as currentTask
      let taskToLoad: number | null = _preferredTaskId ?? null;

      if (!taskToLoad) {
        // If there's a running or pending task, load its detail and start polling
        const activeTask = result.results.find(
          (t) => t.status === 'running' || t.status === 'pending',
        );
        if (activeTask) {
          taskToLoad = activeTask.id;
        } else if (latestTask) {
          // 没有活跃任务时，自动将最新任务（含 stop）设为 currentTask，
          // 确保制品卡片渲染与历史记录中点击"查看"显示的数据一致
          taskToLoad = latestTask.id;
        }
      }

      if (taskToLoad) {
        await fetchTaskDetail(numProjectId, taskToLoad);
        // 同步 URL，方便复制链接分享当前查看的任务
        if (String(taskToLoad) !== urlTaskId) {
          setSearchParams({ taskId: String(taskToLoad) }, { replace: true });
        }
        const task = result.results.find((t) => t.id === taskToLoad);
        if (task?.status === 'running') {
          startPolling(numProjectId, taskToLoad);
        }
      }
    } catch {
      // Silent fail
      setLatestTaskArtifacts(undefined);
    } finally {
      setHistoryLoading(false);
    }
  }, [numProjectId, numPackageId, fetchTaskDetail, startPolling]);

  // Load existing BCR task or history on mount
  useEffect(() => {
    // If URL contains a taskId query param, prefer that specific task
    const preferredTaskId = urlTaskId ? Number(urlTaskId) : null;
    loadExistingTask(preferredTaskId);
    return () => {
      reset();
    };
  }, [numProjectId, numPackageId]);

  // Refresh history list when current task reaches a terminal state
  // Only refresh the list — do NOT auto-switch to active task,
  // otherwise viewing a historical record would be immediately overwritten.
  const taskStatus = currentTask?.status;
  const prevTaskStatusRef = useRef(taskStatus);
  useEffect(() => {
    const prev = prevTaskStatusRef.current;
    prevTaskStatusRef.current = taskStatus;
    // Only trigger when the *same* task transitions to terminal state,
    // not when user switches to view a historical (already terminal) task.
    if (
      prev === 'running' &&
      (taskStatus === 'finished' || taskStatus === 'stop')
    ) {
      refreshHistoryList();
    }
  }, [taskStatus, refreshHistoryList]);

  const buildFileList = () => {
    const fileList: Record<string, string> = {};
    if (currentPackage && 'files' in currentPackage) {
      const files = (currentPackage as { files?: Array<{ file_type: { code: string }; file_url: string }> }).files;
      if (files) {
        for (const f of files) {
          // Convert to uppercase to match backend expectation
          const fileTypeCode = f.file_type.code.toUpperCase();
          // Only add files with valid file_url (Git-based files may have empty file_url)
          if (f.file_url && f.file_url.trim() !== '') {
            fileList[fileTypeCode] = f.file_url;
          }
        }
      }
    }
    return fileList;
  };

  const handleCreate = async () => {
    // 项目尚未生成运行配置（BVP-BAT）时，直接提醒用户先生成，
    // 避免任务触发后因缺少配置在运行阶段失败。
    if (!hasBatConfig) {
      Modal.warning({
        title: '尚未生成运行配置',
        content: `项目「${currentProject?.name || '-'}」尚未生成 BCR 运行配置，请先点击右上角「生成运行配置」完成配置后再发起 BCR 测试。`,
        okText: '我知道了',
      });
      return;
    }
    // Save task data and open selection modal
    setPendingTaskData({
      package_id: numPackageId,
      mf_package_name: mfPackageName.trim(),
      file_list: buildFileList(),
    });
    setTaskModalVisible(true);
  };

  const handleCustomArtifactsChange = async (artifacts: Array<{ kind: string; rmp_kind: string; name: string; url?: string }>) => {
    // Save to package level (shared across all BCR tasks for the same BSW version)
    const formatted = artifacts.map((a) => ({
      kind: a.kind,
      name: a.name,
      url: a.url || '',
    }));
    try {
      const updated = await updatePackageCustomArtifacts(numPackageId, formatted);
      setCurrentPackageDetail(updated);
    } catch {
      message.error('更新制品包失败，请重试');
    }
  };

  const handleGenerateArtifacts = async () => {
    // Trigger BCR with only build task selected to generate artifacts
    if (!currentPackage) return;

    const fileList = buildFileList();
    const customArtifactsForTask = getPackageCustomArtifacts();
    const requestData = {
      package_id: numPackageId,
      mf_package_name: '',
      file_list: fileList,
      selected_tasks: ['building bsw package'], // Only build task
      comparison_config: undefined,
      custom_bsw_package: undefined,
      custom_artifacts: customArtifactsForTask.length > 0 ? customArtifactsForTask : undefined,
      skip_build: false,
    };

    const task = await createTask(numProjectId, requestData);
    if (task) {
      message.success('制品包生成任务已启动');
      startPolling(numProjectId, task.id);
      loadExistingTask();
    } else {
      // createTask 内部吞掉异常只返回 null，失败原因落在 store.error；不主动提示的话
      // 用户点了按钮既没有任务也没有任何反馈（线上 1413 的现象）。
      message.error(useBcrStore.getState().error || 'BCR任务创建失败');
    }
  };

  const handleTaskSelection = async (selectedTasks: string[], comparisonConfig?: ComparisonConfig, customBswPackage?: CustomBswPackage, bvpToolsBranch?: string, showSocBspReport?: boolean, oldPackageName?: string) => {
    setTaskModalVisible(false);

    if (!pendingTaskData) return;

    const requestData = {
      ...pendingTaskData,
      selected_tasks: selectedTasks,
      comparison_config: comparisonConfig,
      custom_bsw_package: customBswPackage,
      custom_artifacts: getPackageCustomArtifacts().length > 0 ? getPackageCustomArtifacts() : undefined,
      skip_build: false,
      bvp_tools_branch: bvpToolsBranch,
      show_soc_bsp_report: showSocBspReport,
      old_package_name: oldPackageName || '',
    };

    const task = await createTask(numProjectId, requestData);

    if (task) {
      message.success('BCR任务创建成功');
      startPolling(numProjectId, task.id);
      // Refresh history
      loadExistingTask();
    } else {
      message.error(useBcrStore.getState().error || 'BCR任务创建失败');
    }

    setPendingTaskData(null);
  };

  const handleCancel = () => {
    if (!currentTask) return;
    Modal.confirm({
      title: '确认终止',
      content: '确定要终止正在运行的BCR测试吗？',
      okText: '终止',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        const success = await cancelTask(numProjectId, currentTask.id);
        if (success) {
          message.success('BCR测试已终止');
          loadExistingTask();
        }
      },
    });
  };

  const handleRerun = (subTaskName: string) => {
    if (!currentTask) return;
    Modal.confirm({
      title: '确认重跑',
      content: `确定要重跑子任务「${subTaskName}」吗？`,
      okText: '重跑',
      cancelText: '取消',
      onOk: async () => {
        const success = await rerunSubTask(numProjectId, currentTask.id, subTaskName);
        if (success) {
          message.success('子任务重跑已启动');
        }
      },
    });
  };

  const handleClose = () => {
    stopPolling();
    navigate(`/packages/${projectId}`);
  };

  const handleCreateRpaRequirement = async () => {
    if (!currentTask) return;
    setIsCreatingRpa(true);
    try {
      const result = await bcrApi.createRpaRequirement(numProjectId, currentTask.id);
      const isUpdate = result.is_update;
      message.success(
        <span>
          {isUpdate ? 'RPA底软版本需求已更新' : 'RPA底软版本需求已创建'}{' '}
          <a href={result.rpa_requirement_url} target="_blank" rel="noopener noreferrer">
            查看详情
          </a>
        </span>,
        5,
      );
      // Refresh task detail to get updated rpa fields
      await fetchTaskDetail(numProjectId, currentTask.id);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      message.error(error?.response?.data?.message || '创建RPA需求失败');
    } finally {
      setIsCreatingRpa(false);
    }
  };

  const handleViewHistory = async (taskId: number) => {
    stopPolling();
    resetLogs();

    // 更新 URL，方便区分和分享
    setSearchParams({ taskId: String(taskId) }, { replace: true });

    // 并行获取任务详情和刷新历史列表，确保状态同步
    await Promise.all([
      fetchTaskDetail(numProjectId, taskId),
      refreshHistoryList()
    ]);

    const task = useBcrStore.getState().currentTask;
    if (task && task.status === 'running') {
      startPolling(numProjectId, taskId);
    }
  };

  const isRunning = currentTask?.status === 'running';
  const hasActiveTask = currentTask && (currentTask.status === 'running' || currentTask.status === 'pending');
  const hasRpaConfig = !!currentProject?.rpa_config?.project_key;

  const historyColumns: ColumnsType<BcrTaskListItem> = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '目标包', dataIndex: 'target_package_name', ellipsis: true },
    {
      title: '创建者',
      dataIndex: 'bvp_creator',
      width: 100,
      render: (bvpCreator: string | null | undefined, record: BcrTaskListItem) =>
        bvpCreator || record.created_by?.display_name || '未知',
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (s: string) => {
        const map: Record<string, { color: string; text: string }> = {
          pending: { color: 'gray', text: '排队中' },
          running: { color: 'orange', text: '运行中' },
          finished: { color: 'green', text: '完成' },
          stop: { color: 'red', text: '终止' },
        };
        const cfg = map[s] || { color: 'gray', text: s };
        return <span style={{ color: cfg.color }}>{cfg.text}</span>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 160,
      render: (d: string) => formatDate(d),
    },
    {
      title: '创建环境',
      dataIndex: 'environment',
      width: 120,
      render: (e: string | null | undefined) => e || '-',
    },
    {
      title: '操作',
      width: 80,
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => handleViewHistory(record.id)}>
          查看
        </Button>
      ),
    },
  ];

  return (
    <div className="p-4">
      <div className="flex items-center gap-4 mb-4">
        <Button icon={<ArrowLeftOutlined />} onClick={handleClose}>
          返回
        </Button>
        <Title level={4} className="!mb-0">
          <ExperimentOutlined className="mr-2" />
          BCR测试 - {currentPackage?.name || `包#${packageId}`}
        </Title>
      </div>

      {error && (
        <Alert title={error} type="error" closable onClose={clearError} className="mb-4" />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left: Create / Current task */}
        <div>
          {/* 制品包卡片 — 始终在最上面，使用包级别的 custom_artifacts（同一底软版本的所有 BCR 任务共享） */}
          {currentTask ? (
            <BcrArtifacts
              key="task"
              uploadedArtifacts={currentTask.uploaded_artifacts}
              customArtifacts={getPackageCustomArtifacts()}
              onCustomArtifactsChange={handleCustomArtifactsChange}
            />
          ) : (
            <BcrArtifacts
              key="create"
              uploadedArtifacts={latestTaskArtifacts}
              customArtifacts={getPackageCustomArtifacts()}
              onGenerateArtifacts={handleGenerateArtifacts}
              isGenerating={isCreating}
              onCustomArtifactsChange={handleCustomArtifactsChange}
            />
          )}

          {/* Create form — only show when no active task */}
          {!hasActiveTask && (
            <Card
              title="发起BCR测试"
              className="mb-4"
              extra={
                <Button
                  icon={<SettingOutlined />}
                  onClick={() => setBatConfigDrawerOpen(true)}
                  disabled={!currentProject}
                >
                  {hasBatConfig ? '更新运行配置' : '生成运行配置'}
                </Button>
              }
            >
              <Descriptions column={1} size="small" className="mb-4">
                <Descriptions.Item label="目标底软包">
                  {currentPackage?.name || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="所属项目">
                  {currentProject?.name || '-'}
                </Descriptions.Item>
              </Descriptions>

              <div className="space-y-4">
                <div>
                  <Text className="block mb-1">MF产物包名称 <span style={{ color: '#999', fontSize: '12px' }}>(可选)</span></Text>
                  <Input
                    placeholder="输入MF产物包名称（可选）"
                    value={mfPackageName}
                    onChange={(e) => setMfPackageName(e.target.value)}
                  />
                </div>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  loading={isCreating}
                  onClick={handleCreate}
                >
                  开始BCR
                </Button>
              </div>
            </Card>
          )}

          {/* Current task detail */}
          {currentTask && (
            <>
              <Card
              title={`BCR任务 #${currentTask.id}`}
              extra={
                <Space>
                  {currentTask.status === 'finished' && (
                    currentTask.rpa_requirement_url ? (
                      <Space>
                        <Button
                          type="link"
                          icon={<LinkOutlined />}
                          onClick={() => setRpaModalVisible(true)}
                        >
                          查看RPA需求
                        </Button>
                        <Button
                          type="primary"
                          disabled={!hasRpaConfig}
                          loading={isCreatingRpa}
                          onClick={handleCreateRpaRequirement}
                          title={!hasRpaConfig ? '请先在系统配置中设置RPA项目空间Key' : undefined}
                        >
                          更新RPA底软版本需求
                        </Button>
                      </Space>
                    ) : (
                      <Button
                        type="primary"
                        disabled={!hasRpaConfig}
                        loading={isCreatingRpa}
                        onClick={handleCreateRpaRequirement}
                        title={!hasRpaConfig ? '请先在系统配置中设置RPA项目空间Key' : undefined}
                      >
                        创建RPA底软版本需求
                      </Button>
                    )
                  )}
                  {isRunning && (
                    <Button
                      danger
                      icon={<StopOutlined />}
                      loading={isCancelling}
                      onClick={handleCancel}
                    >
                      停止
                    </Button>
                  )}
                  <Button onClick={handleClose}>关闭</Button>
                </Space>
              }
            >
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Spin size="large" />
                </div>
              ) : (
                <>
                  <Descriptions column={2} size="small" className="mb-4">
                    <Descriptions.Item label="状态">
                      <span
                        style={{
                          color:
                            currentTask.status === 'running'
                              ? '#faad14'
                              : currentTask.status === 'finished'
                                ? '#52c41a'
                                : currentTask.status === 'stop'
                                  ? '#ff4d4f'
                                  : '#8c8c8c',
                          fontWeight: 600,
                        }}
                      >
                        {currentTask.status === 'running'
                          ? '运行中'
                          : currentTask.status === 'finished'
                            ? '已完成'
                            : currentTask.status === 'stop'
                              ? '已终止'
                              : '排队中'}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label="创建时间">
                      {formatDate(currentTask.created_at)}
                    </Descriptions.Item>
                    <Descriptions.Item label="创建者">
                      {currentTask.bvp_creator ||
                        currentTask.created_by?.display_name ||
                        '未知'}
                    </Descriptions.Item>
                    <Descriptions.Item label="旧版本包">
                      {currentTask.old_package_name}
                    </Descriptions.Item>
                    <Descriptions.Item label="MF产物包">
                      {currentTask.mf_package_name}
                    </Descriptions.Item>
                    {currentTask.custom_bsw_package?.items && currentTask.custom_bsw_package.items.length > 0 && (
                      <>
                        {currentTask.custom_bsw_package.items.map((item, idx) => (
                          <Descriptions.Item key={`${item.package_type}-${idx}`} label={`自定义${item.package_type}包`}>
                            {item.package_name}
                          </Descriptions.Item>
                        ))}
                      </>
                    )}
                  </Descriptions>

                  <Divider titlePlacement="start" plain>
                    子任务进度
                  </Divider>
                  <BcrSubTaskList
                    subTasks={currentTask.sub_tasks}
                    taskStatus={currentTask.status}
                    onRerun={handleRerun}
                    isRerunning={isRerunning}
                    showSocBspReport={currentTask.show_soc_bsp_report ?? false}
                  />

                  <BcrReportLinks reportLinks={currentTask.report_links} />
                </>
              )}
            </Card>

            {/* RPA需求链接弹窗 */}
            <Modal
              title="RPA需求链接"
              open={rpaModalVisible}
              onCancel={() => setRpaModalVisible(false)}
              footer={null}
              width={700}
            >
              <Space orientation="vertical" style={{ width: '100%' }}>
                <Typography.Text strong>底软版本集成</Typography.Text>
                <div>
                  <a href={currentTask.rpa_requirement_url!} target="_blank" rel="noopener noreferrer">
                    {currentTask.rpa_requirement_url}
                  </a>
                </div>
                {currentTask.module_adaptation_requirements && currentTask.module_adaptation_requirements.length > 0 && (
                  <>
                    <Divider style={{ margin: '12px 0' }} />
                    <Typography.Text strong>模块适配需求 ({currentTask.module_adaptation_requirements.length})</Typography.Text>
                    {currentTask.module_adaptation_requirements.map((m, i) => (
                      <div key={m.rpa_id} style={{ marginTop: i > 0 ? 4 : 0 }}>
                        <Typography.Text type="secondary">{m.module_name}：</Typography.Text>
                        <a href={m.rpa_url} target="_blank" rel="noopener noreferrer">
                          {m.rpa_url}
                        </a>
                      </div>
                    ))}
                  </>
                )}
              </Space>
            </Modal>
            </>
          )}

          {!hasActiveTask && !currentTask && !historyLoading && (
            <Card>
              <Empty description="暂无BCR任务，请发起新测试" />
            </Card>
          )}
        </div>

        {/* Right: History */}
        <div>
          <Card title="历史BCR任务" size="small">
            <Table
              size="small"
              columns={historyColumns}
              dataSource={historyTasks}
              rowKey="id"
              loading={historyLoading}
              pagination={false}
              scroll={{ y: 400 }}
            />
          </Card>
        </div>
      </div>

      {/* Log panel at bottom */}
      {currentTask && (
        <LogPanel
          logs={logs}
          isCollapsed={isLogPanelCollapsed}
          onToggleCollapse={() => setLogPanelCollapsed(!isLogPanelCollapsed)}
          taskId={currentTask.id}
        />
      )}

      {/* Task selection modal */}
      <TaskSelectionModal
        visible={taskModalVisible}
        onConfirm={handleTaskSelection}
        onCancel={() => setTaskModalVisible(false)}
        currentPackage={currentPackage}
        packages={packages}
        previousMcuGit={latestTaskMcuGit}
        customArtifacts={getPackageCustomArtifacts()}
      />

      {/* BAT 配置一键生成抽屉 */}
      {currentProject && (
        <BatConfigGenerateDrawer
          open={batConfigDrawerOpen}
          onClose={() => {
            setBatConfigDrawerOpen(false);
            refreshBatConfigExistence();
          }}
          onSaved={refreshBatConfigExistence}
          project={currentProject.name}
          packageId={numPackageId || undefined}
          vehicleModel={
            currentPackageDetail?.vehicle_model || basicPackage?.vehicle_model || undefined
          }
        />
      )}
    </div>
  );
};

export default BcrTestPage;
